import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {PlatformSetting} from '../../../../models/influencer-setting';
import {FormControl, FormGroup} from '@angular/forms';
import {Channel} from '../../../../models/common';
import {InfluencerService} from '../../../services/influencer/influencer.service';
import {TranslateService} from '@ngx-translate/core';
import {NewInfluencerChanel} from '../../../../models/influencer/new-influencer';
import {ChannelStatus, platformIconPath} from '../../../app.constants';

@Component({
  selector: 'platform-field',
  templateUrl: './platform-field.component.html',
  styleUrls: ['./platform-field.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PlatformFieldComponent implements OnInit {
  @Input() channelData: Channel;
  @Output() authorizeClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() modifyChannelResponse: EventEmitter<any> = new EventEmitter<any>();
  @Output() handleChange: EventEmitter<any> = new EventEmitter<any>();

  authChannelForm: FormGroup;
  public platformSetting: PlatformSetting = new PlatformSetting();
  public isIGChannel: boolean;
  private handle = new FormControl(undefined, {
    validators: this.validateChannelSetting.bind(this),
    updateOn: 'blur'
  });
  public isConfirming = false;

  constructor(private influencerService: InfluencerService, private translate: TranslateService) {

  }

  ngOnInit(): void {
    this.platformSetting.platform = this.channelData.platform ? this.channelData.platform.name.toLowerCase() : null;
    this.platformSetting.account = this.channelData.handle;
    this.authChannelForm = new FormGroup({
      handle: this.handle
    });
    if (!this.platformSetting.status) {
      this.platformSetting.status = this.channelData.type || ChannelStatus.UNAUTHORIZE;
    }
    this.isIGChannel = this.channelData.platform.id === platformIconPath.instagram.id ? true : false;
  }

  onAuthorize() {
    let data;
    if (this.platformSetting.platform === 'instagram' || this.platformSetting.platform === 'facebook') {
      this.channelData.handle = this.platformSetting.account;
      data = this.channelData;
      this.authorizeClick.emit({
        channel: 'ig',
        data
      });
    } else {
      this.onFormSubmit();
    }
  }

  onHandleChange(evt) {
    const changedFieldName = this.channelData.id > 0 ? this.channelData.id.toString() : `new_${this.channelData.index}`;
    this.handleChange.emit({
      fieldName: changedFieldName,
      handle: evt
    });
  }

  reAuthorize() {
    this.platformSetting.status = ChannelStatus.UNAUTHORIZE;
    this.channelData.type = ChannelStatus.UNAUTHORIZE;
    this.onAuthorize();
  }

  fieldName() {
    if (!this.channelData) {
      return null;
    }
    return this.channelData.id > 0 ? this.channelData.id.toString() : `new_${this.channelData.index}`;
  }

  get isUnAuthorize() {
    return this.platformSetting.status === ChannelStatus.UNAUTHORIZE;
  }

  get isReAuthorize() {
    return this.platformSetting.status === ChannelStatus.REAUTHORIZE;
  }

  get isAuthorized() {
    return this.platformSetting.status === ChannelStatus.AUTHORIZED;
  }

  get isError() {
    return this.platformSetting.status === ChannelStatus.AUTHORIZE_FAILED;
  }

  onFormSubmit() {
    if (this.platformSetting.account && this.platformSetting.account !== '') {
      const data = {
        id: this.channelData.id,
        handle: this.platformSetting.account,
        type: ChannelStatus.AUTHORIZED,
        platform_id: this.channelData.platform ? this.channelData.platform.id : null,
        blog_id: this.channelData.blogId,
        youtubeType: this.channelData.youtubeType,
        fieldName: this.fieldName()
      };
      this.platformSetting.status = ChannelStatus.AUTHORIZED;
      this.isConfirming = true;
      if (this.channelData.id > 0) {
        this.updateChannel(this.channelData.id, data);
      } else {
        this.channelData.handle = data.handle;
        this.createNewChannel(data);
      }
    }
  }



  private updateChannel(channelId, channelData) {
    this.influencerService.updateInfluencerChannel(channelId, channelData).subscribe((res) => {
      this.isConfirming = false;
      this.modifyChannelResponse.emit({
        status: 'success',
        type: 'update',
        fieldName: channelData.fieldName,
        data: {
          id: channelId,
          handle: channelData.handle
        }
      });
    }, (err) => {
      this.isConfirming = false;
      this.platformSetting.status = ChannelStatus.AUTHORIZE_FAILED;
      this.channelData.type = ChannelStatus.AUTHORIZE_FAILED;
      this.modifyChannelResponse.emit({
        status: 'error',
        fieldError: channelData.fieldName,
        errorCode: err.error.data
      });
    });
  }

  private createNewChannel(channelData) {
    const newChannel = new NewInfluencerChanel(channelData.platform_id, channelData.type);
    newChannel.handle = channelData.handle;
    this.influencerService.addChannelByInfluencer(channelData.blog_id, newChannel).subscribe((res) => {
      this.isConfirming = false;
      this.modifyChannelResponse.emit({
        status: 'success',
        type: 'new',
        handle: channelData.handle,
        fieldName: channelData.fieldName,
        ...res
      });
    }, (err) => {
      this.isConfirming = false;
      this.platformSetting.status = ChannelStatus.AUTHORIZE_FAILED;
      this.channelData.type = ChannelStatus.AUTHORIZE_FAILED;
      this.modifyChannelResponse.emit({
        status: 'error',
        fieldError: channelData.fieldName,
        errorCode: err.error.data
      });
    });
  }

  deleteChannel(channel, isMobile) {
    this.deleteClick.emit({
      channel,
      isMobile
    });
  }

  validateChannelSetting() {
    if (!this.authChannelForm) {
      return null;
    }

    const handle = this.authChannelForm.controls.handle.value;
    if (!handle || `${handle}`.trim() === '') {
      return {
        emptyChannel: true
      };
    }
    return null;
  }

  get isDisabled() {
    return this.channelData.id > 0;
  }

  get unauthorizeButtonLabel() {
    return this.isIGChannel ? this.translate.instant('influencer_settings.status.authorize') : this.translate.instant('confirm.btn');
  }

  get reAuthorizeButtonLabel() {
    return this.isIGChannel ? this.translate.instant('influencer_settings.status.reauthorize') : this.translate.instant('influencer_settings.status.reconfirm');
  }

  get authorizedButtonLabel() {
    if (this.isConfirming) {
      return this.translate.instant('INFLUENCER.campaign.confirm_modal.btn_confirming');
    }
    return this.isIGChannel ? this.translate.instant('influencer_settings.status.authorized') : this.translate.instant('INFLUENCER.campaign.confirm_modal.btn_confirmed');
  }
}



// WEBPACK FOOTER //
// ./src/app/influencer-platform/influencer-settings/platform-field/platform-field.component.ts